import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { toAbsoluteUrl } from '@/lib/absoluteUrl';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  /** Alt text for og:image (link previews / accessibility) */
  imageAlt?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object;
  noIndex?: boolean;
  canonicalUrl?: string;
}

export const SEOHead = ({
  title,
  description,
  keywords,
  image,
  imageAlt,
  url,
  type = 'website',
  structuredData,
  noIndex = false,
  canonicalUrl
}: SEOHeadProps) => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const location = useLocation();

  useEffect(() => {
    if (!config) return;

    const store = selectedStore || config.stores[0];
    const siteName = config.name || store.company || 'Loja de Veículos';
    const siteUrl = config.website || window.location.origin;
    const pageOrigin = (() => {
      const w = (siteUrl || '').trim();
      if (w.startsWith('http')) {
        try {
          return new URL(w).origin;
        } catch {
          /* fall through */
        }
      }
      return window.location.origin;
    })();

    const pathWithSearch = `${location.pathname}${location.search || ''}`;
    const currentUrl = toAbsoluteUrl(
      url || `${pageOrigin}${pathWithSearch}`,
      pageOrigin
    );
    const canonical = toAbsoluteUrl(canonicalUrl || currentUrl, pageOrigin);

    // Default values - Optimized for SEO (shorter titles)
    const defaultTitle = `${siteName} - Sua próxima aventura sobre rodas está a apenas um toque de distância!`;
    const defaultDescription = `Encontre o veículo ideal na ${siteName}. Carros, motos e caminhões usados e seminovos com as melhores condições de pagamento.`;
    const defaultImage = `${pageOrigin}/icon.svg`;

    // Final values - Limit title length to 60 characters for better SEO
    const baseTitle = title || defaultTitle;
    const finalTitle = baseTitle.length > 60 ? 
      `${baseTitle.substring(0, 57)}...` : 
      baseTitle;
    const finalDescription = description || defaultDescription;
    const finalImage = toAbsoluteUrl(image || defaultImage, pageOrigin);
    const finalImageAlt = (imageAlt || finalTitle).slice(0, 200);
    const finalKeywords = keywords || `veículos usados, carros seminovos, ${siteName}, automóveis, motos, caminhões`;

    // Update document title
    document.title = finalTitle;

    // Helper function to update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      
      meta.content = content;
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      
      link.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('author', siteName);
    updateMetaTag('robots', noIndex ? 'noindex,nofollow' : 'index,follow');

    // Open Graph tags
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:image', finalImage, true);
    updateMetaTag('og:image:secure_url', finalImage, true);
    updateMetaTag('og:image:alt', finalImageAlt, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', 'pt_BR', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalImage);
    updateMetaTag('twitter:image:alt', finalImageAlt);
    updateMetaTag('twitter:site', '@' + siteName.replace(/\s+/g, '').toLowerCase());

    // Canonical URL
    updateLinkTag('canonical', canonical);

    // Structured Data (JSON-LD)
    if (structuredData) {
      // Remove existing structured data
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Additional meta tags for better SEO
    updateMetaTag('theme-color', config.themeColors?.primary || '#000000');
    updateMetaTag('msapplication-TileColor', config.themeColors?.primary || '#000000');
    updateMetaTag('apple-mobile-web-app-title', siteName);

    // Language and region
    updateMetaTag('language', 'pt-BR');
    updateMetaTag('geo.region', 'BR');
    updateMetaTag('geo.placename', store.city || 'Brasil');

    // Mobile optimization
    updateMetaTag('format-detection', 'telephone=yes');
    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');

  }, [
    title,
    description,
    keywords,
    image,
    imageAlt,
    url,
    type,
    structuredData,
    noIndex,
    canonicalUrl,
    config,
    selectedStore,
    location.pathname,
    location.search
  ]);

  return null;
};

export default SEOHead;
